import React, { forwardRef, useState, useRef, useEffect } from 'react';
import cn from 'classnames';
import { Tooltip } from 'react-tooltip';
// Helpers
import { CommonInputProps, getCharacterCount } from 'helpers/input';
// Components
import { InputFooter } from 'ui3/InputFooter/InputFooter';
// UI
import MillionsIcon, { IconNames } from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import styles from './Input.module.scss';
import 'react-tooltip/dist/react-tooltip.css';

export type InputSize = 'large' | 'medium' | 'small';

// TODO: this is a bare skeleton of the end icon props-add more props as needed per future use cases
export type EndIconProps = {
  name: IconNames;
  hint?: string;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

// requested by the design to be able to add up to 3 icons at the end of the input.
// these can possibly be info (hint, with tooltip) or action (like password toggle/action button).
type EndIconsArray =
  | []
  | [EndIconProps]
  | [EndIconProps, EndIconProps]
  | [EndIconProps, EndIconProps, EndIconProps];

type InputProps = {
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  name: string;
  fieldSize?: InputSize;
  id?: string;
  icon?: IconNames;
  iconClassName?: string;
  labelClassName?: string;
  endIcons?: EndIconsArray;
} & React.InputHTMLAttributes<HTMLInputElement> &
  CommonInputProps;

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      fieldSize = 'large',
      name,
      id = name,
      className,
      label,
      labelClassName,
      error,
      icon,
      iconClassName,
      type = 'text',
      hint,
      notice,
      maxLength,
      value,
      defaultValue,
      onChange,
      disabled,
      endIcons = [],
      ...restProps
    },
    ref
  ) => {
    const [inputType, setInputType] = useState(type);
    const internalRef = useRef<HTMLInputElement | null>(null);
    const hasError = Boolean(error);
    const isPassword = type === 'password';

    const showCounter = maxLength !== undefined;

    const getInitialValue = (): string => {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          return value.join('');
        }
        return String(value);
      }
      if (defaultValue !== undefined) {
        if (Array.isArray(defaultValue)) {
          return defaultValue.join('');
        }
        return String(defaultValue);
      }
      return '';
    };

    const [inputValue, setInputValue] = useState(getInitialValue);

    const currentLength = getCharacterCount(inputValue);

    useEffect(() => {
      if (value !== undefined) {
        const valueStr = Array.isArray(value) ? value.join('') : String(value);
        setInputValue(valueStr);
      }
    }, [value]);

    useEffect(() => {
      if (value === undefined && internalRef.current) {
        const domValue =
          internalRef.current.value || defaultValue?.toString() || '';
        if (domValue && domValue !== inputValue) {
          setInputValue(domValue);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setRefs = (node: HTMLInputElement | null) => {
      internalRef.current = node;
      if (node && value === undefined) {
        const domValue = node.value || defaultValue?.toString() || '';
        if (domValue && domValue !== inputValue) {
          setInputValue(domValue);
        }
      }
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        // Forward ref - TypeScript can't distinguish MutableRefObject from RefObject
        // This is safe as forwardRef accepts both
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mutableRef = ref as any;
        mutableRef.current = node;
      }
    };

    const handlePasswordVisibilityToggle: React.MouseEventHandler<HTMLButtonElement> = (
      e
    ) => {
      e.preventDefault();

      if (isPassword) {
        setInputType((prevState) =>
          prevState === 'password' ? 'text' : 'password'
        );
      }
    };

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
      onChange && onChange(event);
    };

    const tooltipId = `input-tooltip-${id}`;
    const hasEndIcons = endIcons.length > 0;
    const hasEndAdornment = isPassword || hasEndIcons;

    return (
      <div className={cn(styles.root, className)}>
        {label && (
          <label htmlFor={id} className={cn(styles.label, labelClassName)}>
            {label}
          </label>
        )}

        <div
          className={cn(styles.inputWrapper, {
            [styles.error]: hasError,
            [styles.disabled]: disabled,
            [styles.withEndAdornment]: hasEndAdornment,
          })}
        >
          {icon && (
            <MillionsIcon
              className={cn(styles.icon, styles[fieldSize], iconClassName)}
              name={icon}
            />
          )}
          <input
            name={name}
            data-testid={id}
            id={id}
            ref={setRefs}
            className={cn(styles.input, styles[fieldSize], {
              [styles.error]: hasError,
              [styles.withIcon]: Boolean(icon),
              [styles.withEndAdornment]: hasEndAdornment,
            })}
            type={inputType}
            value={value}
            defaultValue={defaultValue}
            onChange={handleOnChange}
            maxLength={maxLength}
            disabled={disabled}
            {...restProps}
          />
          <div
            className={cn(styles.endAdornment, {
              [styles[fieldSize]]: fieldSize,
            })}
          >
            {endIcons.map((endIcon, index) => {
              const endIconId = `input-end-icon-${id}-${index}`;

              return (
                <React.Fragment key={endIconId}>
                  {endIcon.onClick ? (
                    <button
                      type="button"
                      {...(endIcon.hint && { id: endIconId })}
                      className={cn(
                        styles.endIcon,
                        styles[fieldSize],
                        endIcon.className
                      )}
                      onClick={endIcon.onClick}
                      disabled={disabled}
                      aria-label={endIcon.hint || endIcon.name}
                    >
                      <MillionsIcon name={endIcon.name} />
                    </button>
                  ) : (
                    <div
                      {...(endIcon.hint && { id: endIconId })}
                      className={cn(
                        styles.endIcon,
                        styles[fieldSize],
                        endIcon.className
                      )}
                    >
                      <MillionsIcon name={endIcon.name} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            {isPassword && (
              <button
                type="button"
                className={cn(styles.passwordToggle, styles[fieldSize])}
                onClick={handlePasswordVisibilityToggle}
                disabled={disabled}
                aria-label="toggle password visibility"
              >
                {inputType === 'password' ? (
                  <MillionsIcon name="eyeOff" />
                ) : (
                  <MillionsIcon name="eye" />
                )}
              </button>
            )}
          </div>
        </div>

        {endIcons.map((endIcon, index) => {
          if (!endIcon.hint) return null;
          const endIconId = `input-end-icon-${id}-${index}`;
          return (
            <Tooltip
              key={`tooltip-${endIconId}`}
              anchorId={endIconId}
              place="top"
              className={styles.endIconTooltip}
            >
              {endIcon.hint}
            </Tooltip>
          );
        })}

        <InputFooter
          hint={hint}
          notice={notice}
          error={error}
          showCounter={showCounter}
          currentLength={currentLength}
          maxLength={maxLength}
          tooltipId={tooltipId}
        />
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
